// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import Loader from './Loader';
import RequestUpgradeModal from './RequestUpgradeModal';
import { getSessionUserEmail } from '../config/common';
import * as upgradeRequestListAction from '../Action/Upgrade_Request/Upgrade_Request_List_Action';
import * as requiredListAction from '../Action/Required_List/Required_List_Action';

const PAGE_SIZE = 10;

const fmt = (d) => (d ? dayjs(d).format('YYYY-MM-DD') : '—');

/**
 * The version this request upgrades FROM.
 *
 * Deliberately Old_Version__c and not Current_Version__c. Current_Version__c is a formula that
 * reflects the account's version *today*, not the version at the time of the request — so on a
 * completed historical upgrade it reads as newer than the target and the row looks like a
 * downgrade (real production example: current "Congaree v1" against target "Acadia 5B1").
 * Old_Version__c is stored on the request itself and is the version that was actually upgraded from.
 */
const fromVersion = (item) => item.Old_Version__c || item.Current_Version__c || '—';

/**
 * An upgrade runs in two stages: the customer's SANDBOX is upgraded first, they validate it, and
 * only then is PRODUCTION scheduled. Each stage has its own requested/scheduled/installed dates.
 */
const STAGES = [
    {
        key: 'sandbox',
        label: 'Sandbox',
        requested: 'SB_Requested_Date__c',
        scheduled: 'SB_Scheduled_Date__c',
        installed: 'SB_Installation_Date__c',
    },
    {
        key: 'production',
        label: 'Production',
        requested: 'Prod_Requested_Date__c',
        scheduled: 'Prod_Scheduled_Date__c',
        installed: 'Prod_Install_Date__c',
    },
];

/**
 * The single line the card leads with: what is actually happening next.
 * Walks the stages in order and reports the earliest step not yet done, so the customer does not
 * have to read six dates to answer "where is my upgrade?".
 */
function nextMilestone(item) {
    if (item.Prod_Install_Date__c) {
        return { label: 'Completed', detail: `Production upgraded ${fmt(item.Prod_Install_Date__c)}` };
    }
    for (const stage of STAGES) {
        if (item[stage.installed]) continue;
        if (item[stage.scheduled]) {
            return { label: `${stage.label} scheduled`, detail: fmt(item[stage.scheduled]) };
        }
        if (item[stage.requested]) {
            return { label: `${stage.label} requested`, detail: `Awaiting a scheduled date` };
        }
        return { label: `${stage.label} not yet scheduled`, detail: '' };
    }
    return { label: 'In progress', detail: '' };
}

function StageTrack({ stage, item }) {
    const steps = [
        { label: 'Requested', value: item[stage.requested] },
        { label: 'Scheduled', value: item[stage.scheduled] },
        { label: 'Installed', value: item[stage.installed] },
    ];
    const started = steps.some((s) => s.value);

    return (
        <div className="col-md-6 mb-3">
            <div className="border rounded-3 p-3 h-100">
                <h4 className="copilot-changelog-section-title h6 mb-2">{stage.label}</h4>
                {!started ? (
                    <p className="text-muted small mb-0">Not yet scheduled</p>
                ) : (
                    steps.map((s) => (
                        <p className="text-muted small mb-1" key={s.label}>
                            {s.label}: {fmt(s.value)}
                        </p>
                    ))
                )}
            </div>
        </div>
    );
}

const UpgradeRequests = () => {
    const dispatch = useDispatch();

    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [upgradeRequestList, setUpgradeRequestList] = useState([]);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showRequestModal, setShowRequestModal] = useState(false);
    // Versions for the Request Upgrade dropdown. Same source the Change Log uses, fetched here so
    // the shared modal does not have to depend on whichever screen opened it.
    const [versions, setVersions] = useState([]);
    const [pagination, setPagination] = useState({ pageNo: 1, totalPage: 1 });

    const loadUpgradeRequests = (pageNo = 1) => {
        setIsLoading(true);
        setLoadError('');
        const param = { pageNo, pageSize: PAGE_SIZE };

        dispatch(upgradeRequestListAction.upgrade_request_list({ data: JSON.stringify(param) }))
            .then((response) => {
                setIsLoading(false);
                if (response?.status !== 1) {
                    setLoadError(response?.message || 'Something went wrong, please try again.');
                    return;
                }
                setUpgradeRequestList(response?.data?.updateRequestList || []);
                if (response?.data?.totalPage !== undefined) {
                    setPagination((prev) => ({ ...prev, totalPage: response.data.totalPage || 1 }));
                }
            })
            .catch((error) => {
                setIsLoading(false);
                setLoadError(error?.message || 'Something went wrong, please try again.');
            });
    };

    useEffect(() => {
        loadUpgradeRequests(1);
        dispatch(requiredListAction.required_list({ data: JSON.stringify({ email: getSessionUserEmail() }) }))
            .then((response) => {
                if (response?.status === 1) setVersions(response?.data?.versions || []);
            })
            .catch(() => {
                // The list still works without the dropdown; the modal simply shows no versions.
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const changePage = (pageNo) => {
        setPagination((prev) => ({ ...prev, pageNo }));
        loadUpgradeRequests(pageNo);
    };

    return (
        <>
            {isLoading && <Loader />}

            <div className="container-fluid py-3 px-3 px-md-4">
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                    <div className="min-w-0">
                        <h2 className="copilot-changelog-section-title mb-1">Upgrade Requests</h2>
                        <p className="copilot-changelog-section-lead text-muted small mb-0">
                            Track the upgrades requested for your organization.
                        </p>
                    </div>
                    {/* Same button as the Change Log rail — identical classes, icon and label.
                        `w-100` is intentionally dropped: there it fills a narrow sidebar, here it
                        would stretch the full page width. */}
                    <button
                        type="button"
                        className="btn btn-primary flex-shrink-0"
                        onClick={() => setShowRequestModal(true)}
                    >
                        <i className="fa-solid fa-arrow-up-right-from-square me-2" aria-hidden />
                        Request Upgrade
                    </button>
                </div>

                {loadError ? (
                    <div className="text-center py-4">
                        <p className="text-muted mb-3">{loadError}</p>
                        <button
                            type="button"
                            className="btn btn-primary shrink"
                            onClick={() => loadUpgradeRequests(pagination.pageNo)}
                        >
                            Try again
                        </button>
                    </div>
                ) : !isLoading && upgradeRequestList.length === 0 ? (
                    <div className="text-center py-4">
                        <h6 className="mb-1">No upgrade requests yet</h6>
                        <p className="text-muted small mb-0">
                            When your organization requests an upgrade, it will appear here.
                        </p>
                    </div>
                ) : (
                    upgradeRequestList.map((item, index) => {
                        const milestone = nextMilestone(item);
                        return (
                            <div
                                key={item.Id || index}
                                className="copilot-changelog-card copilot-support-list-card mb-3 cursor-pointer"
                                role="button"
                                tabIndex={0}
                                onClick={() => setSelectedRequest(item)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setSelectedRequest(item);
                                    }
                                }}
                            >
                                <div
                                    className="copilot-changelog-card__accent copilot-changelog-card__accent--muted"
                                    aria-hidden
                                />
                                <div className="copilot-changelog-card__body">
                                    <h3 className="copilot-changelog-section-title text-primary mb-2">{item.Name}</h3>
                                    <div className="row">
                                        <div className="col-md-6">
                                            <p className="text-muted small mb-1">Status: {item.Status__c || '—'}</p>
                                            <p className="text-muted small mb-1">
                                                From version: {fromVersion(item)}
                                            </p>
                                            <p className="text-muted small mb-1">
                                                Target version: {item.Target_Version__c || '—'}
                                            </p>
                                        </div>
                                        <div className="col-md-6">
                                            <p className="text-muted small mb-1">Requested: {fmt(item.CreatedDate)}</p>
                                            <p className="text-muted small mb-1">
                                                Next: {milestone.label}
                                                {milestone.detail ? ` — ${milestone.detail}` : ''}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}

                {pagination.totalPage > 1 && (
                    <div className="row px-2 mb-2">
                        <div className="col-12 background py-2 text-end">
                            <button
                                className="btn btn-primary me-2 shrink"
                                onClick={() => changePage(pagination.pageNo - 1)}
                                disabled={pagination.pageNo === 1}
                            >
                                Previous
                            </button>
                            {pagination.pageNo}&nbsp;/&nbsp;{pagination.totalPage}
                            <button
                                className="btn btn-primary ms-2 shrink"
                                onClick={() => changePage(pagination.pageNo + 1)}
                                disabled={pagination.pageNo >= pagination.totalPage}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <RequestUpgradeModal
                show={showRequestModal}
                versions={versions}
                onClose={() => setShowRequestModal(false)}
                onCreated={() => loadUpgradeRequests(1)}
            />

            {selectedRequest && (
                <>
                    <div className="modal fade show copilot-changelog-upgrade-modal-root" style={{ display: 'block' }} role="dialog">
                        <div className="modal-dialog modal-dialog-centered modal-lg">
                            <div className="modal-content copilot-changelog-upgrade-modal">
                                <div className="copilot-changelog-upgrade-modal__accent" aria-hidden />
                                <div className="modal-header copilot-changelog-upgrade-modal__header border-0">
                                    <div>
                                        <h5 className="copilot-changelog-section-title copilot-changelog-upgrade-modal__title mb-0">
                                            {selectedRequest.Name}
                                        </h5>
                                        <p className="copilot-changelog-section-lead copilot-changelog-upgrade-modal__lead mb-0 mt-1">
                                            {fromVersion(selectedRequest)} &rarr;{' '}
                                            {selectedRequest.Target_Version__c || '—'}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn-close copilot-changelog-upgrade-modal__close flex-shrink-0 mt-0"
                                        aria-label="Close"
                                        onClick={() => setSelectedRequest(null)}
                                    />
                                </div>
                                <div className="modal-body copilot-changelog-upgrade-modal__body">
                                    <p className="text-muted small mb-1">
                                        Status: {selectedRequest.Status__c || '—'}
                                    </p>
                                    {/* Who submitted the request. Rendered only when present:
                                        Requested_By__c was added after this feature shipped, so
                                        requests filed before it have no value — showing "Requested
                                        by: —" on those would look like data was lost rather than
                                        never captured. */}
                                    {selectedRequest['Requested_By__r.Name'] && (
                                        <p className="text-muted small mb-1">
                                            Requested by: {selectedRequest['Requested_By__r.Name']}
                                        </p>
                                    )}
                                    <div className="mb-3" />
                                    <div className="row">
                                        {STAGES.map((stage) => (
                                            <StageTrack key={stage.key} stage={stage} item={selectedRequest} />
                                        ))}
                                    </div>
                                    {selectedRequest.Customer_Comments__c && (
                                        <div className="mt-2">
                                            <h4 className="copilot-changelog-section-title h6 mb-1">Your comments</h4>
                                            <p className="small text-body mb-0">{selectedRequest.Customer_Comments__c}</p>
                                        </div>
                                    )}
                                </div>
                                <div className="modal-footer copilot-changelog-upgrade-modal__footer border-0 gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-primary shrink"
                                        onClick={() => setSelectedRequest(null)}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show copilot-changelog-upgrade-modal-backdrop" />
                </>
            )}
        </>
    );
};

export default UpgradeRequests;
